import React, { useContext, useEffect, useState } from "react";

import ScheduleIcon from "@mui/icons-material/Schedule";
import { Box, Button, CircularProgress, Grid, Tooltip } from "@mui/material";
import { useParams } from "react-router-dom";

import { AdminRestrictionFeature } from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";
import { RestrictionName } from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import SubscriptionAction from "./subscription-action";
import { getOrgRestrictions } from "api/endpoints/restrictions";
import { updateSeatMovementsRestriction } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { OrgDataContext, SubDataContext } from "context/organisation-context";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { upCaseString } from "helpers/utils";
import {
  UpdateSeatMovementsRestrictionWrapperProps,
  UrlParamTypes,
} from "types/org-display";

const convertRestrictionName = (name: RestrictionName) =>
  `RESTRICTION_NAME_${RestrictionName[name]}`;

const restrictionName = convertRestrictionName(
  RestrictionName.SUBSCRIPTIONS_RESTRICT_SEAT_MOVEMENT,
);

const UpdateSeatMovementsRestriction = ({
  subscription,
}: UpdateSeatMovementsRestrictionWrapperProps) => {
  const { regionCode } = useParams<UrlParamTypes>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [restrictSeatMovements, setRestrictSeatMovements] = useState<
    boolean | undefined
  >(undefined);
  const [restrictionLoading, setRestrictionLoading] = useState(true);

  const disableSeatMovementRestrictionsBtn =
    [SubscriptionType.FREE, SubscriptionType.FREE_ORG].includes(
      subscription?.type,
    ) ||
    !subscription?.active ||
    restrictSeatMovements === undefined ||
    restrictionLoading;

  const restrictSeatMovementsString = (
    restrictSeatMovements || false
  ).toString();

  const restrictSeatMovementsToUpdateToString =
    (!restrictSeatMovements).toString();

  const { orgData } = useContext(OrgDataContext);
  const { subData } = useContext(SubDataContext);
  const { showError } = useContext(SnackbarContext);

  const hasFutureDatedComponents = (() => {
    try {
      const doc = JSON.parse(subData?.json ?? "") as {
        zuora?: { pending_future_components?: unknown };
      };
      const pending = doc.zuora?.pending_future_components;
      return Array.isArray(pending) && pending.length > 0;
    } catch {
      return false;
    }
  })();

  const getSeatMovementRestriction = async () => {
    setRestrictionLoading(true);
    try {
      const restrictionsResponse = await getOrgRestrictions(
        {
          organizationId: toUUID(orgData.id),
          restrictionNames: [restrictionName],
        },
        callOptionsHeaders(regionCode, orgData.id),
      );

      const seatMovementRestriction =
        restrictionsResponse.restrictions[restrictionName];
      if (seatMovementRestriction?.restriction.case === "feature") {
        const featureRestriction = seatMovementRestriction.restriction
          .value as AdminRestrictionFeature;
        setRestrictSeatMovements(
          featureRestriction.userRestriction?.isRestricted || false,
        );
      } else {
        setRestrictSeatMovements(false);
      }
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setRestrictionLoading(false);
    }
  };

  useEffect(() => {
    if (orgData?.id) {
      getSeatMovementRestriction();
    }
  }, [orgData?.id, regionCode]);

  const handleUpdateSeatMovementsRestrictionReq = async () => {
    setShowLoadingSpinner(true);
    const newRestrictionValue = !restrictSeatMovements;
    try {
      const updateRestrictionResponse = await updateSeatMovementsRestriction(
        {
          restrictSeatMovements: newRestrictionValue,
          organisationId: orgData.id,
        },
        callOptionsHeaders(regionCode, orgData.id),
      );

      if (updateRestrictionResponse) {
        // Update local restriction state
        setRestrictSeatMovements(
          updateRestrictionResponse.restrictSeatMovements,
        );
      }
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setShowLoadingSpinner(false);
    }
  };

  return (
    <>
      <SubscriptionAction
        title="Restrict Seat Movements:"
        // Wait for subscription and restriction data to be loaded before displaying
        displayInfo={
          subscription && restrictSeatMovements !== undefined ? (
            <>
              {upCaseString(restrictSeatMovementsString)}
              {hasFutureDatedComponents && (
                <Tooltip title="Future-dated change scheduled — seat movements are restricted regardless of this setting">
                  <ScheduleIcon
                    fontSize="small"
                    color="warning"
                    data-testid="seat-movements-future-dated-change-indicator"
                    style={{ verticalAlign: "middle", marginLeft: 4 }}
                  />
                </Tooltip>
              )}
            </>
          ) : undefined
        }
      >
        {showLoadingSpinner || restrictionLoading ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Tooltip
            title={
              hasFutureDatedComponents
                ? "Future-dated change scheduled — seat movements are restricted regardless of this setting"
                : ""
            }
            disableHoverListener={!hasFutureDatedComponents}
            placement="left"
          >
            <Box
              component="span"
              sx={{ display: "block", width: "100%", mt: 1, mr: 2 }}
            >
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={disableSeatMovementRestrictionsBtn}
                onClick={() => setOpenConfirmDialog(true)}
                data-testid="update-seat-movements-reaction-btn"
                endIcon={
                  hasFutureDatedComponents ? (
                    <ScheduleIcon fontSize="small" />
                  ) : undefined
                }
              >
                SET TO {upCaseString(restrictSeatMovementsToUpdateToString)}
              </Button>
            </Box>
          </Tooltip>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Confirm Action"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => handleUpdateSeatMovementsRestrictionReq()}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
      >
        Are you sure you want to set Restrict Seat Movements to{" "}
        {upCaseString(restrictSeatMovementsToUpdateToString)}?
      </ConfirmDialog>
    </>
  );
};

export default UpdateSeatMovementsRestriction;
